import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { SmartToy } from '@mui/icons-material';
import LoadingButton from './LoadingButton';
import { useModels } from '../hooks/useModels';
import { useUserSettings } from '../hooks/useUserSettings';
import { useNotification } from '../hooks/useNotification';

const FIELDS = [
  { key: 'classification', label: 'Email type detection', help: 'Decides what each email is: an alert, a statement, a receipt, a promotion ...' },
  { key: 'extraction', label: 'Reading emails', help: 'Reads transactions, bills and receipts out of emails' },
  { key: 'pdf', label: 'Reading PDF statements', help: 'Reads transactions from uploaded statements' },
];

/**
 * Settings > AI Models (whitelisted users only). An empty choice means the
 * app's default model.
 */
const AIModelSettings = () => {
  const { models, defaultModel, defaultClassificationModel, loading: modelsLoading } = useModels();
  const { settings, update, loading } = useUserSettings();
  const { showSuccess, showError } = useNotification();
  const [values, setValues] = useState({ classification: '', extraction: '', pdf: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.models) {
      setValues({
        classification: settings.models.classification || '',
        extraction: settings.models.extraction || '',
        pdf: settings.models.pdf || '',
      });
    }
  }, [settings]);

  if (loading || modelsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings?.canChooseModels) {
    return <Alert severity="info">Model choice is not available on this account.</Alert>;
  }

  const labelOf = (value) => models.find(m => m.value === value)?.label || value;

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({ models: values });
      showSuccess('AI models saved');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save AI models');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        avatar={<SmartToy color="primary" />}
        title="AI Models"
        subheader="Used for forwarded emails, Gmail imports (including the daily import) and PDF uploads"
      />
      <CardContent>
        <Stack spacing={3}>
          {FIELDS.map(field => (
            <Box key={field.key}>
              <FormControl fullWidth>
                <InputLabel shrink>{field.label}</InputLabel>
                <Select
                  displayEmpty
                  notched
                  value={values[field.key]}
                  label={field.label}
                  onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                >
                  <MenuItem value="">
                    Default ({labelOf(field.key === 'classification' ? defaultClassificationModel : defaultModel)})
                  </MenuItem>
                  {models.map(model => (
                    <MenuItem key={model.value} value={model.value}>{model.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {field.help}
              </Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
              Save
            </LoadingButton>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default AIModelSettings;
